import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import AddMasterInline from "./AddMasterInline";
import { LEGACY_PART, partOptions, toOptions } from "./shared";

export const blankSet = (init = {}) => ({ key: Math.random().toString(36).slice(2), grade_id: "", quantity: "", part_code_id: "", legacy_text: "", ...init });

const Err = ({ msg, testId }) => (msg ? <p className="text-xs text-red-600 mt-1" data-testid={testId}>{msg}</p> : null);

function SetRow({ typeId, set, idx, canRemove, grades, parts, errors, onPatch, onRemove, reload }) {
  const p = `service-entry-type-${typeId}-set-${idx}`;
  const err = errors[set.key] || {};
  return (
    <div className="grid grid-cols-[1.2fr_0.7fr_1.2fr_auto] gap-2 items-start" data-testid={p}>
      <div>
        <div className="flex gap-1.5">
          <SearchableSelect testId={`${p}-grade`} value={set.grade_id} onValueChange={(v) => onPatch({ grade_id: v })} placeholder="Grade *" options={toOptions(grades, `${p}-grade`)} />
          <AddMasterInline kind="grades" noun="Grade" testId={`${p}-grade-add`} onAdded={(n) => { reload(); onPatch({ grade_id: n.id }); }} />
        </div>
        <Err msg={err.grade} testId={`${p}-grade-error`} />
      </div>
      <div>
        <Input type="number" min="0" step="any" placeholder="Qty used *" data-testid={`${p}-qty`} value={set.quantity} onChange={(e) => onPatch({ quantity: e.target.value })} />
        <Err msg={err.quantity} testId={`${p}-qty-error`} />
      </div>
      <div className="flex gap-1.5">
        <SearchableSelect testId={`${p}-partcode`} value={set.part_code_id || (set.legacy_text ? LEGACY_PART : "")}
          onValueChange={(v) => (v === LEGACY_PART ? null : onPatch({ part_code_id: v, legacy_text: "" }))} placeholder="Part Code (optional)"
          options={partOptions(parts, `${p}-partcode`, set.legacy_text, !!(set.part_code_id || set.legacy_text))} />
        <AddMasterInline kind="part-codes" noun="Part Code" testId={`${p}-partcode-add`} onAdded={(n) => { reload(); onPatch({ part_code_id: n.id }); }} />
      </div>
      {canRemove ? (
        <Button type="button" size="icon" variant="ghost" data-testid={`${p}-remove`} onClick={onRemove}><Trash2 className="h-4 w-4 text-red-600" /></Button>
      ) : <span className="w-9" />}
    </div>
  );
}

export function ServicesDoneChecklist({ types, grades, parts, sel, setSel, errors, listError, reload }) {
  const count = Object.keys(sel).length;
  const toggle = (id, on) =>
    setSel((s) => {
      const next = { ...s };
      if (on) next[id] = [blankSet()];
      else delete next[id];
      return next;
    });
  const patchSet = (id, key, patch) => setSel((s) => ({ ...s, [id]: s[id].map((x) => (x.key === key ? { ...x, ...patch } : x)) }));
  const addSet = (id) => setSel((s) => ({ ...s, [id]: [...s[id], blankSet()] }));
  const removeSet = (id, key) => setSel((s) => ({ ...s, [id]: s[id].filter((x) => x.key !== key) }));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-800">Services done</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="service-entry-selected-count">{count} selected</span>
          <span className="text-xs text-slate-500">Missing a type?</span>
          <AddMasterInline kind="service-types" noun="Service Type" testId="service-entry-type-add"
            onAdded={(n) => { reload(); setSel((s) => ({ ...s, [n.id]: [blankSet()] })); }} />
        </div>
      </div>
      <div className="rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="service-entry-type-list">
        {types.length === 0 && <p className="p-3 text-sm text-slate-400">No active Service Types.</p>}
        {types.map((t) => {
          const sets = sel[t.id];
          return (
            <div key={t.id} className={`px-3 py-2 ${sets ? "bg-amber-50/60" : ""}`} data-testid={`service-entry-type-row-${t.id}`}>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox checked={!!sets} onCheckedChange={(v) => toggle(t.id, v === true)} data-testid={`service-entry-type-check-${t.id}`} />
                <span className="text-sm font-medium text-slate-800">{t.name}</span>
              </label>
              {sets && (
                <div className="mt-2 ml-6 space-y-2">
                  {sets.map((s, i) => (
                    <SetRow key={s.key} typeId={t.id} set={s} idx={i} canRemove={i > 0} grades={grades} parts={parts} errors={errors} reload={reload}
                      onPatch={(patch) => patchSet(t.id, s.key, patch)} onRemove={() => removeSet(t.id, s.key)} />
                  ))}
                  <button type="button" className="text-xs font-medium text-amber-700 hover:underline inline-flex items-center" data-testid={`service-entry-type-${t.id}-add-another`} onClick={() => addSet(t.id)}>
                    <Plus className="h-3 w-3 mr-1" /> Add another line for this type
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Err msg={listError} testId="service-entry-list-error" />
    </div>
  );
}
